/**
 * features/comments/CommentFlags.tsx
 * ------------------------------------------------------------------
 * FEATURE #1 — Editor flagging. ONLY editors (and admin/headteacher)
 * reach this screen (menu-gated by canRaiseFlags).
 *
 * A flag is INFORMATIONAL: it records an error so the concerned
 * teacher is informed. There is no "submit revision -> reviewer
 * approves" loop. An editor:
 *   - raises a flag (open)
 *   - marks it acknowledged (teacher has been told / seen)
 *   - resolves it (fixed by editor on the report, or by teacher on
 *     the original marksheet)
 *
 * Flags live in the `reportFlags` collection.
 * ------------------------------------------------------------------
 */
import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  updateDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  // Round 6: needed for notification cleanup query
  getDocs,
  where,
} from 'firebase/firestore';
import {
  Flag,
  Plus,
  Eye,
  X,
  AlertTriangle,
  Filter,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { TermKey } from '@/lib/school.config';
import { raiseFlag as raiseFlagHelper } from '@/lib/raiseFlag';
import { auditFromContext } from '@/lib/audit';
import { Button, Card, PageHeader, Badge } from '@/components/ui/primitives';
import type { CommentFlag, FlagStatus, FlagTarget } from '@/types';

const TARGET_LABELS: Record<FlagTarget, string> = {
  subjectComment: 'Subject comment',
  classTeacherComment: 'Class teacher comment',
  hodComment: 'HOY comment',
  mark: 'Mark',
};

const REASONS = [
  'Spelling / grammar',
  'Inappropriate wording',
  'Inconsistent with marks',
  'Incomplete / missing',
  'Wrong student / subject',
  'Other',
];

const STATUS_STYLE: Record<FlagStatus, string> = {
  open: 'bg-red-100 text-red-700 border-red-200',
  acknowledged: 'bg-amber-100 text-amber-700 border-amber-200',
  resolved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

export default function CommentFlags({
  year,
  term,
}: {
  year: string;
  term: TermKey;
}) {
  const { activeStudents: students, classes, subjects, teachers, users } = useData();
  const { userData } = useAuth();

  // P2: an HOY may only flag within their assigned year groups.
  // Editor / admin / headteacher see every class (unchanged).
  const flagClasses = useMemo(() => {
    const effRoles = userData?.roles?.length ? userData.roles : (userData?.role ? [userData.role] : []);
    if (
      effRoles.includes('hoy') &&
      userData?.hoyForms &&
      userData.hoyForms.length > 0
    ) {
      return classes.filter((c) =>
        userData.hoyForms!.includes(String(c.form)),
      );
    }
    return classes;
  }, [classes, userData?.role, userData?.hoyForms]);

  const [flags, setFlags] = useState<CommentFlag[]>([]);
  const [statusFilter, setStatusFilter] = useState<FlagStatus | 'all'>(
    'open',
  );
  const [showModal, setShowModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // Round 6: state for the Dismiss-without-review confirm modal.
  const [dismissTarget, setDismissTarget] = useState<CommentFlag | null>(
    null,
  );
  const [dismissReason, setDismissReason] = useState('');

  // raise-flag form
  const [fClass, setFClass] = useState('');
  const [fStudent, setFStudent] = useState('');
  const [fTarget, setFTarget] = useState<FlagTarget>('subjectComment');
  const [fSubject, setFSubject] = useState('');
  const [fReason, setFReason] = useState(REASONS[0]);
  const [fNotes, setFNotes] = useState('');

  useEffect(() => {
    const q = query(
      collection(db, COLLECTIONS.flags),
      orderBy('createdAt', 'desc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setFlags(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as object) }) as CommentFlag)
            .filter((f) => f.year === year && f.term === term),
        );
      },
      (e) => setMsg(e.message),
    );
    return unsub;
  }, [year, term]);

  const filtered = useMemo(
    () =>
      statusFilter === 'all'
        ? flags
        : flags.filter((f) => f.status === statusFilter),
    [flags, statusFilter],
  );

  const counts = useMemo(
    () => ({
      open: flags.filter((f) => f.status === 'open').length,
      acknowledged: flags.filter((f) => f.status === 'acknowledged').length,
      resolved: flags.filter((f) => f.status === 'resolved').length,
    }),
    [flags],
  );

  const flagStudents = students
    .filter((s) => s.currentClass === fClass)
    .sort((a, b) => a.fullName.localeCompare(b.fullName));

  function resetForm() {
    setFClass('');
    setFStudent('');
    setFTarget('subjectComment');
    setFSubject('');
    setFReason(REASONS[0]);
    setFNotes('');
  }

  async function raiseFlag() {
    if (!fClass || !fStudent) {
      setMsg('Pick a class and student.');
      return;
    }
    setBusy(true);
    try {
      // Delegate to the shared helper so the existing screen and
      // the new inline icons in ReviewWorkspace write identical
      // flags + notifications + editor routing.
      const result = await raiseFlagHelper({
        year,
        term,
        classId: fClass,
        studentId: fStudent,
        target: fTarget,
        reason: fReason,
        notes: fNotes,
        subjectId: fSubject || undefined,
        classes,
        students,
        subjects,
        teachers,
        users,
        flaggedById: userData?.uid ?? '',
        flaggedByName: userData?.displayName ?? 'Editor',
      });
      setMsg(result.message);
      setShowModal(false);
      resetForm();
    } catch (e) {
      setMsg((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(f: CommentFlag, status: FlagStatus) {
    try {
      const fromStatus = f.status;
      const patch: Partial<CommentFlag> = { status };
      if (status === 'acknowledged')
        patch.acknowledgedAt = new Date().toISOString();
      if (status === 'resolved') {
        patch.resolvedAt = new Date().toISOString();
        patch.resolvedById = userData?.uid;
      }
      await updateDoc(doc(db, COLLECTIONS.flags, f.id), patch);
      // Round 10: audit the status change so the student-history
      // tab and the Activity Log both reflect the lifecycle.
      auditFromContext(userData, 'flag_status_changed', 'academic', {
        targetId: f.id,
        targetType: 'comment',
        targetName: `Flag on ${f.studentName}`,
        details: {
          flagId: f.id,
          fromStatus,
          toStatus: status,
          target: f.target,
          year: f.year,
          term: f.term,
          classId: f.classId,
          className: f.className,
          studentId: f.studentId,
          studentName: f.studentName,
          subjectId: f.subjectId,
          subjectName: f.subjectName,
          reason: f.reason,
        },
      });
    } catch (e) {
      setMsg((e as Error).message);
    }
  }

  // Round 6: deep-link to Comment Review with the flag selected.
  // Mutates the URL hash directly — App.tsx's hashchange listener
  // picks it up and routes the user.
  function openInReview(f: CommentFlag) {
    window.location.hash = `#/commentReview/${f.id}`;
  }

  // Round 6: dismissal flow. A dismissed flag is just a resolved
  // flag that didn't go through Review — the editor decided no
  // further action was needed. We capture the dismissal reason in
  // the audit log so the choice can be reviewed later.
  async function dismissFlag(f: CommentFlag, reason: string) {
    try {
      const fromStatus = f.status;
      await updateDoc(doc(db, COLLECTIONS.flags, f.id), {
        status: 'resolved',
        resolvedAt: new Date().toISOString(),
        resolvedById: userData?.uid ?? '',
        dismissedWithoutReview: true,
        dismissReason: reason || 'No reason provided',
      });
      auditFromContext(userData, 'flag_dismissed', 'academic', {
        targetId: f.id,
        targetType: 'comment',
        targetName: `Flag on ${f.studentName}`,
        details: {
          flagId: f.id,
          fromStatus,
          toStatus: 'resolved',
          dismissReason: reason,
          target: f.target,
          year: f.year,
          term: f.term,
          classId: f.classId,
          className: f.className,
          studentId: f.studentId,
          subjectId: f.subjectId,
          subjectName: f.subjectName,
        },
      });
      // Round 6: auto-clear any notifications keyed to this flag.
      // Best effort — failure here doesn't break the dismissal.
      try {
        const snap = await getDocs(
          query(
            collection(db, COLLECTIONS.notifications),
            where('flagId', '==', f.id),
          ),
        );
        await Promise.all(
          snap.docs.map((d) =>
            updateDoc(doc(db, COLLECTIONS.notifications, d.id), {
              read: true,
              readAt: new Date().toISOString(),
              clearedByFlagResolution: true,
            }),
          ),
        );
      } catch (cleanupErr) {
        console.warn(
          '[CommentFlags] notification cleanup failed:',
          (cleanupErr as Error).message,
        );
      }
      setDismissTarget(null);
      setDismissReason('');
      setMsg('Flag dismissed.');
    } catch (e) {
      setMsg((e as Error).message);
    }
  }

  return (
    <div>
      <PageHeader
        title="Comment Flags"
        subtitle="Editors flag errors to inform teachers. Corrections are made by editors on the report or by teachers on the original marksheet."
        actions={
          <Button
            icon={<Plus size={16} />}
            onClick={() => setShowModal(true)}
          >
            Raise flag
          </Button>
        }
      />

      {msg && (
        <div className="mb-4 text-sm rounded-xl px-3 py-2.5 border bg-blue-50 text-blue-700 border-blue-200 flex items-center justify-between">
          <span>{msg}</span>
          <button onClick={() => setMsg(null)}>
            <X size={14} />
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4 text-sm">
        <Filter size={14} className="text-gray-400" />
        {(['open', 'acknowledged', 'resolved', 'all'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full border capitalize ${
              statusFilter === s
                ? 'bg-primary text-white border-primary'
                : 'border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300'
            }`}
          >
            {s}
            {s !== 'all' && (
              <span className="ml-1.5 opacity-70">
                {counts[s as FlagStatus]}
              </span>
            )}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Card className="p-12 text-center text-sm text-gray-500">
          <Flag size={28} className="mx-auto mb-3 text-gray-300" />
          No {statusFilter !== 'all' ? statusFilter : ''} flags for{' '}
          {term.replace('Term', 'Term ')} {year}.
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((f) => (
            <Card key={f.id} className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={STATUS_STYLE[f.status]}>
                      {f.status}
                    </Badge>
                    <span className="text-xs text-gray-400">
                      {TARGET_LABELS[f.target]}
                    </span>
                  </div>
                  <p className="font-semibold mt-1.5 text-gray-800 dark:text-gray-100">
                    {f.studentName}{' '}
                    <span className="font-normal text-gray-400">
                      · {f.className}
                      {f.subjectName ? ` · ${f.subjectName}` : ''}
                    </span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 flex items-center gap-1.5">
                    <AlertTriangle
                      size={14}
                      className="text-amber-500 shrink-0"
                    />
                    {f.reason}
                    {f.notes ? ` — ${f.notes}` : ''}
                  </p>
                  <p className="text-xs text-gray-400 mt-1.5">
                    Flagged by {f.flaggedByName}
                    {f.teacherName
                      ? ` · informs ${f.teacherName}`
                      : ''}
                  </p>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  {/* Round 6: Review is the primary action — the only
                      legitimate way to resolve a flag is to verify the
                      underlying comment in context. Dismiss is the
                      escape hatch for genuinely-not-an-issue flags. */}
                  {f.status !== 'resolved' && (
                    <Button
                      icon={<Eye size={14} />}
                      onClick={() => openInReview(f)}
                      className="!py-1.5 !px-3 text-xs"
                    >
                      Review
                    </Button>
                  )}
                  {f.status !== 'resolved' && (
                    <Button
                      variant="ghost"
                      icon={<X size={14} />}
                      onClick={() => setDismissTarget(f)}
                      className="!py-1.5 !px-3 text-xs"
                    >
                      Dismiss
                    </Button>
                  )}
                  {f.status === 'resolved' && (
                    <Button
                      variant="ghost"
                      onClick={() => setStatus(f, 'open')}
                      className="!py-1.5 !px-3 text-xs"
                    >
                      Reopen
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-y-auto">
          <Card glass className="w-full max-w-md p-6 animate-fade-in my-8">
            <h2 className="text-lg font-bold mb-4">Raise a flag</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Class</label>
                <select
                  value={fClass}
                  onChange={(e) => {
                    setFClass(e.target.value);
                    setFStudent('');
                  }}
                  className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all"
                >
                  <option value="">— select —</option>
                  {flagClasses.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Student</label>
                <select
                  value={fStudent}
                  disabled={!fClass}
                  onChange={(e) => setFStudent(e.target.value)}
                  className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all disabled:opacity-50"
                >
                  <option value="">— select —</option>
                  {flagStudents.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.fullName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">What's flagged</label>
                <select
                  value={fTarget}
                  onChange={(e) =>
                    setFTarget(e.target.value as FlagTarget)
                  }
                  className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all"
                >
                  {Object.entries(TARGET_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              {(fTarget === 'subjectComment' || fTarget === 'mark') && (
                <div>
                  <label className="text-sm font-medium">Subject</label>
                  <select
                    value={fSubject}
                    onChange={(e) => setFSubject(e.target.value)}
                    className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all"
                  >
                    <option value="">— select —</option>
                    {subjects.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="text-sm font-medium">Reason</label>
                <select
                  value={fReason}
                  onChange={(e) => setFReason(e.target.value)}
                  className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all"
                >
                  {REASONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">
                  Notes (what to fix)
                </label>
                <textarea
                  value={fNotes}
                  onChange={(e) => setFNotes(e.target.value)}
                  rows={3}
                  className="w-full mt-1 px-3 py-2.5 rounded-xl border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all resize-y"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="ghost"
                onClick={() => setShowModal(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button onClick={raiseFlag} loading={busy}>
                Raise flag
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Round 6: Dismiss-without-review confirm modal */}
      {dismissTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => {
            setDismissTarget(null);
            setDismissReason('');
          }}
        >
          <div
            className="w-full max-w-md"
            onClick={(e: { stopPropagation: () => void }) => e.stopPropagation()}
          >
            <Card className="p-6">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-500" />
              Dismiss without review?
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
              You're closing this flag without opening the comment in
              Review. Use this when the flag is a non-issue (raised in
              error, duplicate, etc.). For real corrections click
              <strong> Review </strong> instead.
            </p>
            <div className="mb-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 text-xs text-gray-600 dark:text-gray-300">
              <strong>{dismissTarget.studentName}</strong> ·{' '}
              {TARGET_LABELS[dismissTarget.target]}
              {dismissTarget.subjectName
                ? ` · ${dismissTarget.subjectName}`
                : ''}
              <br />
              <span className="text-gray-400">
                Reason: {dismissTarget.reasonLabel || dismissTarget.reason}
              </span>
            </div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Why are you dismissing this?
            </label>
            <input
              type="text"
              value={dismissReason}
              onChange={(e) => setDismissReason(e.target.value)}
              placeholder="e.g. raised in error, duplicate of another flag…"
              className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 focus:border-primary focus:outline-none text-sm mb-4"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setDismissTarget(null);
                  setDismissReason('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => dismissFlag(dismissTarget, dismissReason)}
                disabled={!dismissReason.trim()}
              >
                Dismiss flag
              </Button>
            </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
